// Client-side validation rules for user details: the registration flow
// (RegisterPage.jsx) and profile edits (ProfilePage.jsx).
//
// These rules exist for UX only -- instant feedback and a disabled submit button.
// They are trivially bypassed by calling the API directly, so the backend MUST
// enforce the same rules itself (see UserBase / UserUpdate / validate_person_name /
// validate_mobile_number / validate_password_strength in backend/server.py).
// Keep the two in sync when changing either side.

// local-part@domain.tld: exactly one "@", no whitespace anywhere, domain made of
// dot-separated labels (letters/digits/hyphens, not starting or ending with a
// hyphen), and an alphabetic TLD of at least 2 characters.
const EMAIL_PATTERN = /^[^\s@]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export const isValidEmail = (value) => EMAIL_PATTERN.test(value.trim());

// 1-50 characters of letters, spaces, hyphens and apostrophes. The minimum is 1
// so single-initial surnames ("S") are accepted. \p{L} (any Unicode
// letter) rather than A-Z so names like "José" or "Zoë" aren't rejected, and both
// the straight (') and curly (’) apostrophe are accepted -- iOS/macOS keyboards
// insert the curly one by default ("O’Brien"). Must contain at least one letter so
// input made only of punctuation ("--") doesn't pass.
const NAME_PATTERN = /^[\p{L} '’-]{1,50}$/u;

export const isValidName = (value) => {
  const trimmed = value.trim();
  return NAME_PATTERN.test(trimmed) && /\p{L}/u.test(trimmed);
};

// Optional "+" then 8-15 digits (the E.164 maximum). Common separators (spaces,
// hyphens, parentheses) are stripped before checking, so "+91 98765 43210" is
// accepted the same as "+919876543210" -- the backend accepts these characters too.
const MOBILE_PATTERN = /^\+?\d{8,15}$/;

export const isValidMobile = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return true; // optional field: empty is valid
  return MOBILE_PATTERN.test(trimmed.replace(/[\s()-]/g, ''));
};

// The same 5 checks, in the same order, as validate_password_strength on the
// backend. "Special character" means anything that isn't a letter or a digit,
// matching the backend's [^A-Za-z0-9]. The backend also rejects common passwords
// from a blocklist, which the client can't check -- that surfaces as a server error.
export const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'upper', label: 'At least one uppercase letter (A–Z)', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'At least one lowercase letter (a–z)', test: (pw) => /[a-z]/.test(pw) },
  { id: 'digit', label: 'At least one digit (0–9)', test: (pw) => /\d/.test(pw) },
  { id: 'special', label: 'At least one special character (e.g., ! @ # $ % ^ & *)', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export const checkPasswordRules = (password) =>
  PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(password) }));

// Strength comes from how many of the 5 rules are met:
// 0-2 = Weak, 3-4 = Medium, 5 = Strong.
export const passwordStrength = (metCount) => {
  if (metCount >= PASSWORD_RULES.length) return 'strong';
  if (metCount >= 3) return 'medium';
  return 'weak';
};
